<template>
    <div>
        <page-header
            :text="$t('navigation.systems.overview')"
            :searchField="{
                placeholder: $t('systems.search'),
                inputEmitEvent: 'search',
                allowedFields: searchFields,
                nameTranslation: 'system',
                savedSearchName: 'search-system-overview',
            }"
            :button="
                $can('csv export', 'Systems')
                    ? {
                          text: $t('systems.export_csv'),
                          emitEvent: 'exportCSV',
                          ability: systemsAvailable,
                          loading: csvLoading,
                      }
                    : false
            "
            @exportCSV="exportCSVHandler"
            @search="searchHandler"
        />

        <template>
            <div class="subheader subheader--small">
                <div class="row">
                    <div class="column-tablet-16">
                        <h2 class="subheader__headline">{{ $t('overview.activated_systems') }}</h2>
                    </div>

                    <div class="column-tablet-8 align-right" v-if="$can('activate', 'System') && typeof userData !== 'undefined' && userData.organization && userData.organization.role !== 'person'">
                        <a class="button button--large" @click="showActivationDialog()">
                            <i class="icon icon--action-add"></i>
                            <span>{{ $t('overview.activate_new_system') }}</span>
                        </a>
                    </div>
                </div>
            </div>

            <div class="table-wrapper">
                <table class="table table--striped" v-if="activatedSystems.length > 0" id="overview.activated_systems">
                    <thead>
                        <tr>
                            <th
                                v-for="field in activated.fields"
                                :key="field.value"
                                class="table-filter__trigger"
                                :class="{ 'hide-until-tablet': field.value === 'breadcrumbs' || field.value === 'labels', 'wider-mobile': field.value === 'name' || field.value === 'address1' }"
                                :width="field.width"
                            >
                                <div>{{ field.title }}</div>
                                <div
                                    v-if="field.sortable"
                                    class="table-filter__sorting"
                                    @click="sortClickHandler('activated', field.value)"
                                    :class="{
                                        asc: activated.activeSorting[field.value] === 'asc',
                                        desc: activated.activeSorting[field.value] === 'desc',
                                    }"
                                >
                                    <i class="icon icon--common-caret"></i>
                                </div>
                            </th>
                            <th width="8%" v-if="$can('delete', 'System')">
                                {{ $t('buttons.delete') }}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr
                            v-for="(system, index) in activatedSystems"
                            :key="system.id"
                            class="table__row table__row--clickable"
                            @click.exact="$router.push('/systems/' + system.id)"
                            @click.middle="openWindow('/systems/' + system.id)"
                            @click.ctrl="openWindow('/systems/' + system.id)"
                            @click.meta="openWindow('/systems/' + system.id)"
                        >
                            <td class="hide-until-tablet">
                                <span v-if="system.service_organization">
                                    <OrganizationBreadcrumbs :organization="system.service_organization" :breadcrumbs="system.service_organization.breadcrumbs" />
                                </span>
                            </td>
                            <td>
                                <div v-if="system.service_organization" class="hide-from-tablet tiny-text brick-zero--dwarf--bottom unclickable">
                                    <OrganizationBreadcrumbs :organization="system.service_organization" :breadcrumbs="system.service_organization.breadcrumbs" />
                                </div>
                                <b>{{ system.name }}</b>
                            </td>
                            <td>{{ formatSystemAddress(system) }}</td>

                            <td class="hide-until-tablet">
                                <SystemLabels :system="system" />
                            </td>

                            <td>{{ system.serial_number }}</td>

                            <td>{{ system.mac_address }}</td>

                            <td class="centered">
                                <alarm-message :alarm="system.latest_alarm" :tooltip="true" />
                            </td>
                            <td v-if="$can('delete', 'System')">
                                <a class="button button--attention" :class="{ 'button--disabled': system.deleting }" @click.stop="deleteSystem(system)">
                                    <i class="icon icon--action-decline" v-if="!system.deleting"></i>
                                    <i v-else class="edit-popup__loader icon icon--common-spinner spinner" :class="{ 'spinner--finished': !system.deleting }"></i>
                                    <span>{{ $t('buttons.delete') }}</span>
                                </a>
                            </td>
                        </tr>
                    </tbody>

                    <TablePagination
                        v-if="activated.paginationData"
                        :paginationData="activated.paginationData"
                        :visibleAmount="activated.searchedSystems ? activated.searchedSystems.length : activated.systems.length"
                        @goToPrevPage="prevPageHandler('activated')"
                        @goToNextPage="nextPageHandler('activated')"
                    />
                </table>
            </div>
        </template>

        <template v-if="$can('see', 'Registered systems') || pending.searchedSystems.length > 0">
            <header class="subheader subheader--small">
                <div class="row">
                    <div class="column-tablet-16">
                        <h2 class="subheader__headline">{{ $t('overview.registered_systems') }}</h2>
                    </div>

                    <div class="column-tablet-8 align-right" v-if="$can('register', 'System')">
                        <a class="button button--large" @click="showRegistrationDialog()">
                            <i class="icon icon--action-add"></i>
                            <span>{{ $t('overview.register_new_system') }}</span>
                        </a>
                    </div>
                </div>
            </header>

            <div class="table-wrapper">
                <table class="table table--striped" v-if="pendingSystems.length > 0" id="overview.registered_systems">
                    <thead>
                        <tr>
                            <th v-for="field in pending.fields" :key="field.value" class="table-filter__trigger" :width="field.width">
                                <div>{{ field.title }}</div>
                                <div
                                    v-if="field.sortable"
                                    class="table-filter__sorting"
                                    @click="sortClickHandler('pending', field.value)"
                                    :class="{
                                        asc: pending.activeSorting[field.value] === 'asc',
                                        desc: pending.activeSorting[field.value] === 'desc',
                                    }"
                                >
                                    <i class="icon icon--common-caret"></i>
                                </div>
                            </th>
                            <th width="20%"></th>
                            <th width="8%" v-if="$can('delete', 'Registered system')"></th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr
                            v-for="system in pendingSystems"
                            :key="system.id"
                            class="table__row table__row--clickable"
                            @click.exact="$router.push('/systems/' + system.id)"
                            @click.middle="openWindow('/systems/' + system.id)"
                            @click.ctrl="openWindow('/systems/' + system.id)"
                            @click.meta="openWindow('/systems/' + system.id)"
                        >
                            <td>{{ system.serial_number }}</td>
                            <td>{{ system.mac_address }}</td>
                            <td>
                                <span class="button button--primary">{{ $t('systems.manage') }}</span>
                                <span>&nbsp;</span>
                                <button v-if="$can('activate', 'Registered system')" class="button button--success" @click.stop="showActivationDialog(system)">
                                    {{ $t('systems.activate') }}
                                </button>
                            </td>
                            <td v-if="$can('delete', 'Registered system')">
                                <a class="button button--attention" :class="{ 'button--disabled': system.deleting }" @click.stop="deleteSystem(system)">
                                    <i class="icon icon--action-decline" v-if="!system.deleting"></i>
                                    <i v-else class="edit-popup__loader icon icon--common-spinner spinner" :class="{ 'spinner--finished': !system.deleting }"></i>
                                    <span>{{ $t('buttons.delete') }}</span>
                                </a>
                            </td>
                        </tr>
                    </tbody>

                    <TablePagination
                        v-if="pending.paginationData && !pending.searchedSystems"
                        :paginationData="pending.paginationData"
                        :visibleAmount="pending.systems.length"
                        @goToPrevPage="prevPageHandler('pending')"
                        @goToNextPage="nextPageHandler('pending')"
                    />
                </table>
            </div>
        </template>
    </div>
</template>

<script>
import * as api from '@/api';
import showActivationDialog from '@/mixins/showActivationDialog';
import openWindow from '@/mixins/openWindow';
import PageHeader from '@/components/PageHeader';
import AlarmMessage from '@/components/AlarmMessage';
import debounce from 'lodash/debounce';
import { createSearchParams } from '@/utils/searchParameters';
import { startCSVDownload, prepareCSVData } from '@/utils/csvExport';
import TablePagination from '@/components/TablePagination';
import SystemLabels from '@/components/SystemLabels';
import { formatAddress } from '@/utils/format';
import OrganizationBreadcrumbs from '@/components/OrganizationBreadcrumbs';
import { mapGetters } from 'vuex';

export default {
    name: 'system-overview',
    mixins: [showActivationDialog, openWindow],
    components: {
        PageHeader,
        AlarmMessage,
        TablePagination,
        SystemLabels,
        OrganizationBreadcrumbs,
    },
    data() {
        return {
            csvLoading: false,
            activeSearchTerm: false,
            activated: {
                systems: [],
                paginationData: false,
                searchedSystems: false,
                activeSorting: false,
                fields: [
                    {
                        value: 'breadcrumbs',
                        title: this.$t('systems.organizations'),
                        sortable: false,
                        width: '15%',
                    },
                    {
                        value: 'name',
                        title: this.$t('systems.system'),
                        sortable: true,
                        width: '18%',
                    },
                    {
                        value: 'address1',
                        title: this.$t('systems.address'),
                        sortable: true,
                        width: '20%',
                    },
                    {
                        value: 'labels',
                        title: this.$t('fields.labels'),
                        sortable: false,
                        width: '15%',
                    },
                    {
                        value: 'serial_number',
                        title: this.$t('fields.serial_number'),
                        sortable: true,
                    },
                    {
                        value: 'mac_address',
                        title: this.$t('fields.mac_address'),
                        sortable: true,
                    },
                    {
                        value: 'latest_alarm',
                        title: this.$t('systems.logs.message'),
                        sortable: false,
                    },
                ],
            },
            pending: {
                systems: [],
                paginationData: false,
                searchedSystems: false,
                activeSorting: false,
                fields: [
                    {
                        value: 'serial_number',
                        title: this.$t('fields.serial_number'),
                        sortable: true,
                    },
                    {
                        value: 'mac_address',
                        title: this.$t('fields.mac_address'),
                        sortable: true,
                    },
                ],
            },
            searchFields: [
                'name',
                'addr',
                'label',
                'sn',
                'mac',
                'errorcode',
                'city',
                'postcode',
                'country',
                'owner',
                'resident',
                'operator',
                'technician',
                'component',
                'hw',
                'sw',
                'hours',
                'starts',
                'consumption',
                'outputRating',
            ],
            searchFieldsPending: ['mac', 'sn'],
        };
    },
    watch: {
        $route: 'fetchSystems', // call the method again if the route changes
    },
    computed: {
        systemsAvailable() {
            return this.pending.systems.length > 0 || this.activated.systems.length > 0;
        },
        activatedSystems() {
            return this.activated.searchedSystems ? this.activated.searchedSystems : this.activated.systems;
        },
        pendingSystems() {
            return this.pending.searchedSystems ? this.pending.searchedSystems : this.pending.systems;
        },
        ...mapGetters(['userData', 'permissions']),
    },
    created() {
        this.fetchSystems();
        if (this.permissions('predictive')) this.searchFields.push('predictivecode');
    },
    methods: {
        prevPageHandler(systemType = 'activated') {
            if (systemType === 'activated') {
                const activePage = this.activated.paginationData ? this.activated.paginationData.current_page : 0;
                if (this.activeSearchTerm) {
                    this.searchSystems(this.activeSearchTerm, activePage - 1, systemType);
                } else {
                    this.fetchActivated(activePage - 1);
                }
            } else {
                const activePage = this.pending.paginationData ? this.pending.paginationData.current_page : 0;
                if (this.activeSearchTerm) {
                    this.searchSystems(this.activeSearchTerm, activePage - 1, systemType);
                } else {
                    this.fetchPending(activePage - 1);
                }
            }
        },
        nextPageHandler(systemType = 'activated') {
            if (systemType === 'activated') {
                const activePage = this.activated.paginationData ? this.activated.paginationData.current_page : 0;
                if (this.activeSearchTerm) {
                    this.searchSystems(this.activeSearchTerm, activePage + 1);
                } else {
                    this.fetchActivated(activePage + 1);
                }
            } else {
                const activePage = this.pending.paginationData ? this.pending.paginationData.current_page : 0;
                if (this.activeSearchTerm) {
                    this.searchSystems(this.activeSearchTerm, activePage + 1, systemType);
                } else {
                    this.fetchPending(activePage + 1);
                }
            }
        },
        searchHandler: debounce(function(e) {
            if (e !== '') {
                this.activeSearchTerm = e;
                this.searchSystems(e, 1, 'activated');
                this.searchSystems(e, 1, 'pending');
            } else {
                this.activeSearchTerm = false;
                this.activated.searchedSystems = false;
                this.pending.searchedSystems = false;
                this.fetchSystems();
            }
        }, 300),
        sortClickHandler(type, field) {
            if (this[type].activeSorting[field] === 'asc') {
                this[type].activeSorting = {};
                this[type].activeSorting[field] = 'desc';
            } else {
                this[type].activeSorting = {};
                this[type].activeSorting[field] = 'asc';
            }
            if (this.activeSearchTerm) {
                this.searchSystems(this.activeSearchTerm, this.activePage);
            } else {
                this.fetchSystems(this.activePage);
            }
        },
        async exportCSVHandler() {
            if (this.csvLoading) return;
            const fields = [
                {
                    name: this.$t('systems.system'),
                    value: 'name',
                },
                {
                    name: this.$t('fields.organization_name'),
                    value: 'service_organization.name',
                },
                {
                    name: this.$t('fields.enabled_at'),
                    value: 'activated_at',
                    action: 'cutdate',
                },
                {
                    name: this.$t('systems.address'),
                    value: 'address1',
                },
                {
                    name: this.$t('fields.postcode'),
                    value: 'postcode',
                },
                {
                    name: this.$t('fields.city'),
                    value: 'city',
                },
                {
                    name: this.$t('fields.country'),
                    value: 'country',
                    action: 'uppercase',
                },
                {
                    name: this.$t('fields.serial_number'),
                    value: 'serial_number',
                },
                {
                    name: this.$t('fields.mac_address'),
                    value: 'mac_address',
                },
            ];
            this.csvLoading = true;
            try {
                let response;
                const itemsPerPage = 100;
                const allowedParams = this.searchFields;
                if (this.activeSearchTerm) {
                    const searchParams = createSearchParams(this.activeSearchTerm, allowedParams);
                    const params = {
                        ...searchParams,
                        per_page: itemsPerPage,
                    };
                    response = await api.systems.search(params);
                } else {
                    response = await api.systems.activated({ per_page: itemsPerPage });
                }

                let systems = response ? response.data.systems : false;

                if (response.data._metadata && response.data._metadata.total_count > itemsPerPage) {
                    const pages = response.data._metadata.total_pages;
                    const startPage = response.data._metadata.current_page + 1; // we already have the results for the first page

                    for (let i = startPage; i <= pages; i++) {
                        let request;
                        if (this.activeSearchTerm) {
                            const searchParams = createSearchParams(this.activeSearchTerm, allowedParams);

                            const params = {
                                ...searchParams,
                                per_page: itemsPerPage,
                                page: i,
                            };
                            request = await api.systems.search(params);
                        } else {
                            request = await api.systems.activated({
                                per_page: itemsPerPage,
                                page: i,
                            });
                        }

                        if (request.data.systems) {
                            if (!systems) systems = [];
                            systems = [...systems, ...request.data.systems];
                        }
                    }
                }

                if (systems) {
                    const csvData = prepareCSVData(fields, systems);
                    startCSVDownload(csvData, 'export-activated-systems');
                    this.csvLoading = false;
                }
            } catch (error) {
                console.error(error);
                this.csvLoading = false;
            }
        },
        async searchSystems(searchTerm, page = 1, systemType = 'activated') {
            const allowedParams = systemType === 'pending' ? this.searchFieldsPending : this.searchFields;
            const searchParams = createSearchParams(searchTerm, allowedParams);
            if (Object.keys(searchParams).length <= 0) {
                this[systemType].searchedSystems = false;
                this[systemType].systems = false;
                return;
            }
            try {
                const params = {
                    ...searchParams,
                    page: page,
                };
                if (systemType === 'pending') {
                    params.pending = true;
                }
                const response = await api.systems.search(params);
                if (response && response.data) {
                    if (systemType === 'activated') {
                        this.activated.searchedSystems = response.data.systems;
                        this.activated.paginationData = response.data._metadata;
                    } else {
                        this.pending.searchedSystems = response.data.systems;
                        this.pending.paginationData = response.data._metadata;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        },
        fetchSystems() {
            this.fetchPending();
            this.fetchActivated();
        },
        fetchSystemsDebounced: debounce(function() {
            this.fetchSystems();
        }, 500),
        fetchActivated(page = 1) {
            let params = { page: page };
            if (Object.keys(this.activated.activeSorting).length > 0) {
                const field = Object.keys(this.activated.activeSorting)[0];
                params.order = `${field}${this.activated.activeSorting[field] === 'asc' ? '' : ' desc'}`;
            }
            api.systems.activated(params).then(
                response => {
                    this.activated.systems = response.data.systems;
                    this.activated.paginationData = response.data._metadata;
                },
                error => {
                    console.error(error);
                    // TODO: show error message
                }
            );
        },
        fetchPending(page = 1) {
            let params = { page: page };
            if (Object.keys(this.pending.activeSorting).length > 0) {
                const field = Object.keys(this.pending.activeSorting)[0];
                params.order = `${field}${this.pending.activeSorting[field] === 'asc' ? '' : ' desc'}`;
            }
            api.systems.pending(params).then(
                response => {
                    this.pending.systems = response.data.systems;
                    this.pending.paginationData = response.data._metadata;
                },
                error => {
                    console.error(error);
                    // TODO: show error message
                }
            );
        },
        formatSystemAddress(system) {
            return formatAddress(system);
        },
        showRegistrationDialog() {
            this.$modal.show(() => import('@/views/modal/RegisterSystem.vue'), null, {
                name: 'RegisterSystem',
                scrollable: true,
                width: 450,
                height: 'auto',
                class: 'ngdialog-theme-winconnect',
            });
        },
        deleteSystem(system) {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('systems.delete_registered_system'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: this.$t('buttons.delete'),
                    successCallback: () => {
                        this.$set(system, 'deleting', true);

                        api.systems
                            .delete({ id: system.id })
                            .then(() => {
                                this.$set(system, 'deleting', false);
                            })
                            .catch(error => {
                                console.error(error);
                                this.$set(system, 'deleting', false);
                                this.$notify({
                                    type: 'error',
                                    text: this.$t('errors.error_occurred'),
                                });
                            })
                            .finally(() => {
                                if (this.activeSearchTerm) {
                                    this.searchSystems(this.activeSearchTerm, this.activePage, 'activated');
                                    this.searchSystems(this.activeSearchTerm, this.activePage, 'pending');
                                } else {
                                    this.fetchSystems();
                                }
                            });
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
    },
};
</script>
